/**
 * Безопасное извлечение текста ошибки для отображения в UI.
 * Никогда не возвращает "{}" или "[object Object]".
 */

const SUPABASE_ERROR_MAP = [
  { match: /user already registered|already exists|already been registered/i, message: 'Этот email уже зарегистрирован. Войдите или используйте другой email.' },
  { match: /invalid login credentials|invalid credentials/i, message: 'Неверный email или пароль' },
  { match: /email not confirmed/i, message: 'Email не подтверждён. Проверьте почту и подтвердите регистрацию.' },
  { match: /email rate limit|too many requests/i, message: 'Слишком много запросов. Попробуйте позже.' },
  { match: /signup requires a valid password|password.*weak|password.*short/i, message: 'Пароль слишком слабый. Используйте минимум 8 символов.' },
  { match: /error sending confirmation email/i, message: 'Не удалось отправить письмо подтверждения. Попробуйте позже или войдите, если уже регистрировались.' },
  { match: /network|fetch failed|failed to fetch|timeout/i, message: 'Ошибка сети. Проверьте подключение к интернету.' },
];

/**
 * Переводит известные ошибки Supabase/API в понятный русский текст.
 * @param {string} rawMessage
 * @returns {string}
 */
function mapKnownError(rawMessage) {
  if (!rawMessage || typeof rawMessage !== 'string') {
    return '';
  }

  for (const { match, message } of SUPABASE_ERROR_MAP) {
    if (match.test(rawMessage)) {
      return message;
    }
  }

  return rawMessage;
}

/**
 * Извлекает строку ошибки из произвольного значения.
 * @param {unknown} error
 * @param {string} [fallback='Произошла ошибка. Попробуйте ещё раз.']
 * @returns {string}
 */
export function formatErrorMessage(error, fallback = 'Произошла ошибка. Попробуйте ещё раз.') {
  if (error == null) {
    return fallback;
  }

  if (typeof error === 'string') {
    const trimmed = error.trim();
    if (!trimmed || trimmed === '{}' || trimmed === '[object Object]') {
      return fallback;
    }
    return mapKnownError(trimmed);
  }

  if (error instanceof Error) {
    return formatErrorMessage(error.message, fallback);
  }

  if (typeof error === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (error);

    if (typeof obj.message === 'string' && obj.message.trim()) {
      return formatErrorMessage(obj.message, fallback);
    }

    if (typeof obj.error === 'string' && obj.error.trim()) {
      return formatErrorMessage(obj.error, fallback);
    }

    if (typeof obj.error_description === 'string' && obj.error_description.trim()) {
      return formatErrorMessage(obj.error_description, fallback);
    }

    if (typeof obj.msg === 'string' && obj.msg.trim()) {
      return formatErrorMessage(obj.msg, fallback);
    }

    if (typeof obj.code === 'string' && obj.code.trim()) {
      return mapKnownError(obj.code) || fallback;
    }
  }

  const asString = String(error).trim();
  if (!asString || asString === '{}' || asString === '[object Object]') {
    return fallback;
  }

  return mapKnownError(asString);
}

export default formatErrorMessage;
